'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, RotateCcw, Variable, Eye, EyeOff } from 'lucide-react';

interface Template {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: 'rejection' | 'hot-lead' | 'follow-up';
  description: string;
}

const defaultTemplates: Template[] = [
  {
    id: 'tmpl-rejection',
    name: 'Professional Rejection',
    subject: 'Re: Your Quote Request — {{first_name}}',
    body: `Hi {{first_name}},

Thank you for reaching out to {{business_name}} about your {{project_type}} project at {{address}}.

After reviewing your request, we unfortunately aren't able to take on new projects in your area at this time. Our current schedule is fully booked through {{next_availability}}.

We'd recommend reaching out to a few other local contractors to get competitive quotes. If your timeline is flexible, feel free to resubmit in 4–6 weeks and we'll be happy to reassess.

We appreciate your interest and wish you the best with your project.

Warm regards,
{{owner_name}}
{{business_name}}
{{business_phone}}`,
    type: 'rejection',
    description: 'Sent automatically to Low leverage leads or manually to any lead you decline.',
  },
  {
    id: 'tmpl-hot-lead',
    name: 'Hot Lead Follow-Up',
    subject: '🔥 Your {{project_type}} Estimate — Ready to Move Forward?',
    body: `Hi {{first_name}},

I just reviewed your quote request for the {{project_type}} at {{address}} — and I have to say, this looks like a great project.

Based on your photo and project details, we're looking at an estimated range of {{estimate_min}}–{{estimate_max}}. This estimate is based on our current pricing and what I can see from the photo you submitted.

Given your timeline of {{timeline}}, I'd like to get a site visit scheduled in the next day or two so we can lock in your exact quote and get on the calendar before our schedule fills up.

👉 To book your free on-site estimate, reply to this email or call me directly at {{business_phone}}.

Looking forward to working with you.

{{owner_name}}
{{business_name}}
{{business_phone}}`,
    type: 'hot-lead',
    description: 'Sent automatically to High leverage leads within 30 minutes of submission.',
  },
  {
    id: 'tmpl-followup',
    name: 'Medium Lead Nurture',
    subject: 'Following Up on Your {{project_type}} Quote',
    body: `Hi {{first_name}},

Just following up on the quote request you submitted for your {{project_type}} project.

We had a chance to review your photo and details. Your project looks straightforward and we'd love to get it done for you. Our estimated range based on your submission is {{estimate_min}}–{{estimate_max}}.

No pressure at all — when you're ready to move forward or have questions, just reply here or give us a call at {{business_phone}}.

We're booking projects {{next_availability}} and would love to hold a spot for you.

Best,
{{owner_name}}
{{business_name}}`,
    type: 'follow-up',
    description: 'Sent to Medium leverage leads 24 hours after submission if no response.',
  },
];

const variables = [
  { token: '{{first_name}}', desc: "Client\'s first name" },
  { token: '{{business_name}}', desc: 'Your business name' },
  { token: '{{business_phone}}', desc: 'Your contact phone' },
  { token: '{{owner_name}}', desc: 'Your name' },
  { token: '{{project_type}}', desc: 'Type of project' },
  { token: '{{address}}', desc: 'Project address' },
  { token: '{{estimate_min}}', desc: 'Low end of AI estimate' },
  { token: '{{estimate_max}}', desc: 'High end of AI estimate' },
  { token: '{{timeline}}', desc: "Client\'s stated timeline" },
  { token: '{{next_availability}}', desc: 'Your next open slot' },
];

export default function MessageTemplateSettings() {
  const [templates, setTemplates] = useState<Template[]>(defaultTemplates);
  const [activeTemplateId, setActiveTemplateId] = useState('tmpl-rejection');
  const [saving, setSaving] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [unsaved, setUnsaved] = useState<Set<string>>(new Set());

  const activeTemplate = templates.find((t) => t.id === activeTemplateId)!;

  const handleChange = (field: keyof Template, value: string) => {
    setTemplates((prev) =>
      prev.map((t) => t.id === activeTemplateId ? { ...t, [field]: value } : t)
    );
    setUnsaved((prev) => new Set(prev).add(activeTemplateId));
  };

  const handleSave = async (id: string) => {
    setSaving(id);
    // TODO: PATCH /api/templates/:id — update Supabase message_templates table
    await new Promise((r) => setTimeout(r, 700));
    setSaving(null);
    setUnsaved((prev) => { const n = new Set(prev); n.delete(id); return n; });
    toast.success('Template saved successfully');
  };

  const handleReset = (id: string) => {
    const original = defaultTemplates.find((t) => t.id === id);
    if (!original) return;
    setTemplates((prev) => prev.map((t) => t.id === id ? { ...original } : t));
    setUnsaved((prev) => { const n = new Set(prev); n.delete(id); return n; });
    toast.success('Template reset to default');
  };

  const insertVariable = (token: string) => {
    const textarea = document.getElementById('template-body') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const current = activeTemplate.body;
    const newBody = current.substring(0, start) + token + current.substring(end);
    handleChange('body', newBody);
    setTimeout(() => {
      textarea.selectionStart = start + token.length;
      textarea.selectionEnd = start + token.length;
      textarea.focus();
    }, 10);
  };

  const previewBody = activeTemplate.body
    .replace(/{{first_name}}/g, 'Marcus')
    .replace(/{{business_name}}/g, 'Austin Pro Painters')
    .replace(/{{business_phone}}/g, '(512) 555-0198')
    .replace(/{{owner_name}}/g, 'Jordan Wells')
    .replace(/{{project_type}}/g, 'Exterior Painting')
    .replace(/{{address}}/g, '4821 Barton Creek Blvd')
    .replace(/{{estimate_min}}/g, '$4,800')
    .replace(/{{estimate_max}}/g, '$6,200')
    .replace(/{{timeline}}/g, 'Within 1 week')
    .replace(/{{next_availability}}/g, 'May 5th');

  const typeColors = {
    rejection: 'text-red-400 bg-red-500/10 border-red-500/20',
    'hot-lead': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    'follow-up': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 max-w-screen-xl">
      {/* Template selector */}
      <div className="xl:col-span-1 space-y-3">
        <h3 className="text-white font-700 text-sm mb-3">Templates</h3>
        {templates.map((tmpl) => (
          <button
            key={tmpl.id}
            onClick={() => setActiveTemplateId(tmpl.id)}
            className={`w-full text-left p-4 rounded-xl border transition-all duration-150 ${
              activeTemplateId === tmpl.id
                ? 'bg-blue-600/10 border-blue-600/30' :'bg-[#0D0D0D] border-[#1F1F1F] hover:border-[#2A2A2A]'
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-white font-600 text-sm">{tmpl.name}</span>
              {unsaved.has(tmpl.id) && (
                <span className="flex-shrink-0 w-2 h-2 rounded-full bg-amber-500 mt-1" title="Unsaved changes" />
              )}
            </div>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-600 border ${typeColors[tmpl.type]}`}>
              {tmpl.type === 'hot-lead' ? 'Hot Lead' : tmpl.type === 'rejection' ? 'Rejection' : 'Nurture'}
            </span>
            <p className="text-[#555] text-xs mt-2 leading-relaxed">{tmpl.description}</p>
          </button>
        ))}

        {/* Variables reference */}
        <div className="p-4 bg-[#0D0D0D] border border-[#1F1F1F] rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Variable size={14} className="text-blue-400" />
            <span className="text-white text-xs font-600">Available Variables</span>
          </div>
          <div className="space-y-1.5">
            {variables.map(({ token, desc }) => (
              <button
                key={`var-${token}`}
                onClick={() => insertVariable(token)}
                className="w-full text-left flex items-center justify-between group hover:bg-[#1A1A1A] rounded px-2 py-1 transition-colors"
              >
                <code className="text-blue-400 text-[11px] font-mono">{token}</code>
                <span className="text-[#555] text-[10px] group-hover:text-[#888] transition-colors">{desc}</span>
              </button>
            ))}
          </div>
          <p className="text-[#444] text-[10px] mt-3 leading-relaxed">Click a variable to insert it at your cursor position in the body.</p>
        </div>
      </div>

      {/* Editor */}
      <div className="xl:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-700 text-base">{activeTemplate.name}</h3>
            {unsaved.has(activeTemplateId) && (
              <span className="text-amber-400 text-xs font-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                Unsaved changes
              </span>
            )}
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1.5 leverage-btn-secondary text-xs py-1.5 px-3"
          >
            {showPreview ? <EyeOff size={13} /> : <Eye size={13} />}
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>

        {!showPreview ? (
          <>
            {/* Subject line */}
            <div>
              <label className="leverage-label">Email Subject Line</label>
              <input
                value={activeTemplate.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                className="leverage-input"
                placeholder="Subject line..."
              />
            </div>

            {/* Body */}
            <div>
              <label className="leverage-label">Message Body</label>
              <textarea
                id="template-body"
                value={activeTemplate.body}
                onChange={(e) => handleChange('body', e.target.value)}
                rows={18}
                className="leverage-input resize-none font-mono text-xs leading-relaxed"
                placeholder="Write your template here. Use variables like {{first_name}} to personalize."
              />
              <p className="text-[#444] text-xs mt-1">
                {activeTemplate.body.length} characters · {activeTemplate.body.split('\n').length} lines
              </p>
            </div>
          </>
        ) : (
          <div className="leverage-card p-5 space-y-4">
            <div>
              <p className="text-[#555] text-xs uppercase tracking-wide font-500 mb-1">Subject Preview</p>
              <p className="text-white font-600 text-base">
                {activeTemplate.subject
                  .replace(/{{first_name}}/g, 'Marcus')
                  .replace(/{{project_type}}/g, 'Exterior Painting')}
              </p>
            </div>
            <hr className="border-[#1F1F1F]" />
            <div>
              <p className="text-[#555] text-xs uppercase tracking-wide font-500 mb-3">Body Preview</p>
              <pre className="text-[#CCC] text-sm leading-relaxed whitespace-pre-wrap font-sans">{previewBody}</pre>
            </div>
          </div>
        )}

        {/* Save row */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => handleReset(activeTemplateId)}
            className="flex items-center gap-1.5 text-[#555] hover:text-[#AAA] text-sm font-500 transition-colors"
          >
            <RotateCcw size={13} />
            Reset to default
          </button>
          <button
            onClick={() => handleSave(activeTemplateId)}
            disabled={saving === activeTemplateId}
            className="leverage-btn-primary text-sm py-2.5 px-6 flex items-center gap-2"
            style={{ minWidth: '130px' }}
          >
            {saving === activeTemplateId ? (
              <><Loader2 size={14} className="animate-spin" /> Saving...</>
            ) : (
              <><Save size={14} /> Save Template</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}